import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { BookProvider, useBook } from './contexts/BookContext';
import CreateBook from './pages/CreateBook';

// ============================================
// LOGIN PAGE
// ============================================
function LoginPage() {
  const { user, loading: authLoading, login } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect to home if already logged in
  useEffect(() => {
    if (!authLoading && user) {
      navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const handleGoogleLogin = async () => {
    setLoading(true);
    setError('');
    try {
      await login();
      // Navigation will happen via the useEffect above once auth state updates
    } catch (err) {
      setError('Failed to sign in. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth state
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  // If already logged in, don't render login page (useEffect will redirect)
  if (user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col items-center justify-center">
      <div className="text-center max-w-md px-6 w-full">
        <div className="mb-8">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">AI Ebook Creator</h1>
          <p className="text-lg text-gray-600">
            Create professional KDP-ready ebooks with AI-powered content generation
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Welcome</h2>
          <p className="text-gray-500 mb-6">Sign in to start creating your books</p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
              {error}
            </div>
          )}

          <button
            onClick={handleGoogleLogin}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 px-6 py-3 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 transition-all font-medium text-gray-700 disabled:opacity-50"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            {loading ? 'Signing in...' : 'Continue with Google'}
          </button>

          <p className="mt-6 text-xs text-gray-400">
            Your books are saved securely to Firebase Cloud
          </p>
        </div>
      </div>
    </div>
  );
}

// ============================================
// PROTECTED ROUTE WRAPPER
// ============================================
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

// ============================================
// HOME PAGE (for signed-in users)
// ============================================
function HomePage() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { actions } = useBook();
  const [showSaved, setShowSaved] = useState(false);
  const [projectList, setProjectList] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  const fetchProjects = async () => {
    setLoadingProjects(true);
    const list = await actions.listSavedProjects();
    setProjectList(list);
    setLoadingProjects(false);
  };

  useEffect(() => {
    if (showSaved) {
      fetchProjects();
    }
  }, [showSaved]);

  const handleLoad = (projectData) => {
    actions.loadProject(projectData);
    navigate('/create');
  };

  const handleDelete = async (name) => {
    await actions.deleteProject(name);
    fetchProjects();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex flex-col items-center justify-center">
      <div className="text-center max-w-2xl px-6 w-full">
        {/* User bar */}
        <div className="flex items-center justify-end mb-4">
          <div className="flex items-center gap-3 bg-white rounded-full shadow-sm px-4 py-2">
            {user?.photoURL && (
              <img src={user.photoURL} alt="" className="w-8 h-8 rounded-full" />
            )}
            <span className="text-sm text-gray-700 font-medium">{user?.displayName || user?.email}</span>
            <button
              onClick={logout}
              className="text-sm text-red-500 hover:text-red-700 font-medium ml-2"
            >
              Sign out
            </button>
          </div>
        </div>

        <div className="mb-8">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">AI Ebook Creator</h1>
          <p className="text-xl text-gray-600">
            Create professional KDP-ready ebooks with AI-powered content generation
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Features</h2>
          <ul className="text-left text-gray-700 space-y-3">
            <li className="flex items-start"><span className="text-green-500 mr-2">✓</span><span>KDP-optimized titles with subtitles for Amazon search</span></li>
            <li className="flex items-start"><span className="text-green-500 mr-2">✓</span><span>12-chapter outline with reader transformation arc</span></li>
            <li className="flex items-start"><span className="text-green-500 mr-2">✓</span><span>AI editor: Expand, Rewrite, Improve, and Humanize</span></li>
            <li className="flex items-start"><span className="text-green-500 mr-2">✓</span><span>Front &amp; back matter: Intro, Copyright, About Author</span></li>
            <li className="flex items-start"><span className="text-green-500 mr-2">✓</span><span>KDP metadata: Description, 7 keywords, categories</span></li>
            <li className="flex items-start"><span className="text-green-500 mr-2">✓</span><span>KDP compliance checker before export</span></li>
            <li className="flex items-start"><span className="text-green-500 mr-2">✓</span><span>Export EPUB + print-ready PDF with proper structure</span></li>
            <li className="flex items-start"><span className="text-green-500 mr-2">✓</span><span>Auto-saves to Firebase — access from any device</span></li>
          </ul>

          <button
            onClick={() => { actions.resetProject(); navigate('/create'); }}
            className="mt-8 w-full flex items-center justify-center px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-semibold"
          >
            ✨ Start New Ebook
          </button>

          <button
            onClick={() => setShowSaved(s => !s)}
            className="mt-4 w-full flex items-center justify-center px-6 py-3 border-2 border-primary-600 text-primary-600 rounded-lg hover:bg-primary-50 transition-colors font-semibold"
          >
            📂 {showSaved ? 'Hide' : 'Load'} Saved Books
          </button>

          {showSaved && (
            <div className="mt-4 text-left">
              {loadingProjects ? (
                <p className="text-center text-gray-500 py-4">Loading your books...</p>
              ) : projectList.length === 0 ? (
                <p className="text-center text-gray-400 py-4">No saved books found.</p>
              ) : (
                <div className="space-y-3">
                  {projectList.map(p => (
                    <div key={p.name} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-primary-300 transition-colors">
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-gray-900 truncate">{p.name}</div>
                        <div className="text-sm text-gray-500">
                          Step {p.step}/6 · {p.chaptersGenerated} chapters written · {p.savedAt ? new Date(p.savedAt).toLocaleDateString() : ''}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4 flex-shrink-0">
                        <button
                          onClick={() => handleLoad(p.data)}
                          className="px-3 py-1 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
                        >
                          Open
                        </button>
                        <button
                          onClick={() => handleDelete(p.name)}
                          className="px-3 py-1 bg-red-100 text-red-600 text-sm rounded-lg hover:bg-red-200 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <p className="mt-6 text-sm text-gray-500">
          Powered by Gemini 2.5 Flash · Books saved to Firebase Cloud
        </p>
      </div>
    </div>
  );
}

// ============================================
// APP
// ============================================
function App() {
  return (
    <AuthProvider>
      <BookProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
            <Route path="/create" element={<ProtectedRoute><CreateBook /></ProtectedRoute>} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </BookProvider>
    </AuthProvider>
  );
}

export default App;